import { Button } from '@creditornot/cb-button';
import { cssThemedValue, typographyCss } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Location } from 'history';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Prompt, useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { WoltModal } from 'common/components';

type Props = {
  when: boolean;
  onConfirm: () => void;
};

const StyledModal = styled(WoltModal)`
  max-width: 448px;
  background: ${cssThemedValue('surfaceMain')};
  border-radius: 8px;
`;

const StyledModalContent = styled(ModalContent)`
  ${typographyCss.Body3};
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const FileMatcherConfirmationPrompt = ({ when, onConfirm }: Props) => {
  const history = useHistory();
  const { pathname, search } = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [nextUrl, setNextUrl] = useState<string>();

  const shouldGoToNextLocation = useCallback(
    ({ pathname: nextPathname, search: nextSearch }: Location) => {
      if (!isOpen) {
        if (pathname + search !== nextPathname + nextSearch) {
          setNextUrl(nextPathname + nextSearch);
          setIsOpen(true);
          return false;
        }
      }
      return true;
    },
    [isOpen, pathname, search],
  );

  const handleConfirm = useCallback(() => {
    if (nextUrl) {
      onConfirm();
      history.push(nextUrl);
      setIsOpen(false);
    }
  }, [history, nextUrl, onConfirm]);

  const handleCancel = () => setIsOpen(false);

  return (
    <>
      <Prompt when={when} message={shouldGoToNextLocation} />
      <StyledModal isOpen={isOpen} onRequestClose={handleCancel}>
        <ModalHeader onCloseClick={handleCancel}>
          <FormattedMessage id="menu.bulk-image.matcher-prompt-header" />
        </ModalHeader>
        <StyledModalContent>
          <FormattedMessage id="menu.bulk-image.matcher-prompt-body" />
        </StyledModalContent>
        <StyledModalFooter>
          <Button variant="lightBlue" size="small" onClick={handleCancel}>
            <FormattedMessage id="menu.bulk-image.matcher-prompt-cancel" />
          </Button>
          <Button variant="blue" size="small" onClick={handleConfirm} style={{ marginLeft: 16 }}>
            <FormattedMessage id="menu.bulk-image.matcher-prompt-confirm" />
          </Button>
        </StyledModalFooter>
      </StyledModal>
    </>
  );
};

export default FileMatcherConfirmationPrompt;
